/*!
 * adc.js — the adc() cue: audio input as a signal source
 * © 2025 Rob Canning — GPLv3
 *
 * Named after Pd/Max's adc~. Starts the live input analyser and publishes its
 * measurements on the control plane, so a score can be driven by what it hears:
 *
 *   adc()                        start listening, publish as adc.*
 *   adc(uid:room)                publish as room.* instead
 *   adc(ch:l, uid:left)          listen to the left channel only
 *   adc(ch:r, uid:right)         …and the right, as a separate source
 *   adc(off:1)                   stop
 *   adc(gain:4)                  four times as loud, before anything is
 *                                measured or drawn; gain:follow(knob.t, 0, 8)
 *   adc(floor:-40)               the noise gate: below -40 dB (-50 by default) the
 *                                input is silence -- no hiss blown up into the
 *                                level, the bands, the scope; floor:off none
 *
 *   viz(adc, view:polar)         its box: from the console, the input draws a
 *                                scope as a synth does -- every view, trail:,
 *                                cell:, effects (waveform:none: no box)
 *   adc(ch:l, uid:left)  adc(ch:r, uid:right)
 *   viz(left, view:xy, y:right)  a stereo input's left against its right
 *
 *   synth(uid:s1, freq: follow(adc.pitch, 90, 2000))
 *   scale(uid:box, sx: map(left.amp, 0, 0.4, 0.5, 3))
 *
 * Channels: amp peak centroid onset low mid high pitch note pitchConf
 * (see control/ports.js, which lists what each carries).
 *
 * TWO CUES, TWO SOURCES. A stereo pair is two adc() cues with different uids,
 * the way adc~ gives you two outlets to patch separately. They share one
 * getUserMedia, so the performer is asked for the microphone once. On a mono
 * input both channels carry the same signal and a warning says so.
 *
 * BROWSERS REQUIRE A GESTURE. getUserMedia prompts for permission and is
 * refused unless something the user did led here, so an adc() that fires from
 * the playhead at load may be blocked. Triggering it from a button() — or any
 * cue the performer sets off — is the reliable way in. The failure is logged
 * rather than thrown, so a score without microphone permission still runs.
 */

import {
  startAudioAnalysis, stopAudioAnalysis, setAnalysisUid, isAudioAnalysisRunning,
  getAnalysisUid,
} from "../control/audioAnalysis.js";
import { parseChannel } from "../control/adcChannel.js";
import { tapScopeAnalyser, inputTrouble, setTapGain, setTapFloor } from "../control/audioAnalysis.js";
import { isBindable, bindParam, releaseBinding } from "../control/paramBinding.js";
import { renderScope, startScope, stopScope } from "../system/scope.js";
import { createLiveAudioRect, removeLiveAudioRect } from "./audio/audioFile.js";
import { registerAnalyser, unregisterAnalyser, markPending } from "./audio/liveAnalysers.js";
import { onReset } from "../system/teardown.js";

/** The inputs drawn: uid -> { handle, analyser, liveRect } (the box's id is scope-<uid>). */
const drawn = new Map();

/**
 * The input's scope, drawn as a synth's is: in a box of its own on the stage
 * (a console line), or into the cue's element (a score's, waveform:self).
 * Its analyser is found by uid too, so viz(s1, view:xy, y:left) can draw
 * the input against a sound. Run again, the box is kept where viz() put it;
 * a uid pointed at another channel (a new tap) draws that one in it.
 * waveform:none: no box, but found all the same (as a synth's is).
 */
function drawInput(name, cueElement, waveform) {
  const analyser = tapScopeAnalyser(name);
  if (!analyser) return;
  const had = drawn.get(name);
  if (had?.analyser === analyser) return;
  if (had) unregisterAnalyser(name, had.analyser);
  registerAnalyser(name, analyser);
  if (had) {
    if (had.handle) startScope(had.handle, analyser);
    had.analyser = analyser;
    return;
  }
  if (/^(none|0)$/.test(waveform)) { drawn.set(name, { analyser }); return; }
  const scopeUid = `scope-${name}`;
  const svg = cueElement?.ownerSVGElement
    || document.querySelector("#scoreInner svg") || document.querySelector(".oscilla-score-inner svg");
  if (!svg) { drawn.set(name, { analyser }); return; }
  const liveRect = cueElement ? null : createLiveAudioRect(svg, scopeUid);
  const handle = renderScope(svg, cueElement ? waveform : liveRect, scopeUid, {
    element: cueElement || liveRect,
    wave: "sine",
    color: liveRect?._waveformColor,
  });
  if (handle) startScope(handle, analyser);
  drawn.set(name, { handle, analyser, liveRect });
}

/** floor: on the tap: dB (-50 by default), off / none for no gate. */
function applyFloor(name, floor) {
  if (floor == null) return;
  setTapFloor(name, /^(off|none)$/i.test(String(floor)) ? -Infinity : Number(floor));
}

/**
 * gain: on the tap -- a number, or follow(...) (a fader, a MIDI knob): kept
 * writing as the signal moves. A number given later lets the follow go.
 */
function applyGain(name, gain) {
  if (gain == null) return;
  const owner = `adc-gain:${name}`;
  if (isBindable(gain)) bindParam(gain, g => setTapGain(name, g), { owner, min: 0, max: 64, default: 1 });
  else { releaseBinding(owner); setTapGain(name, Number(gain)); }
}

/** The input's scope, stopped and taken away (with its box). */
function undrawInput(name) {
  const d = drawn.get(name);
  if (!d) return;
  if (d.handle) stopScope(d.handle);
  if (d.liveRect) removeLiveAudioRect(`scope-${name}`);
  unregisterAnalyser(name, d.analyser);
  drawn.delete(name);
}

// the input released some other way (the transport's mic button): its boxes go too
addEventListener("oscilla:audioAnalysis", (e) => {
  if (e.detail?.state === "stopped") for (const name of [...drawn.keys()]) undrawInput(name);
});
// A project switch releases the input (the next project opens it again if it
// wants it) and forgets its boxes: kept, an adc() of the same name in the
// next project found its analyser drawn and drew no box
onReset("project", function releaseInput() {
  if (isAudioAnalysisRunning()) stopAudioAnalysis();
  for (const name of [...drawn.keys()]) undrawInput(name);
});

export async function handleAdcCue(ast, cueElement = null) {
  const p = {};
  for (const a of (ast?.args || [])) if (a?.type) p[a.type] = a.value;

  const uid = p.uid ? String(p.uid) : null;

  // Tag the element with its uid, the way scale/rotate/drag do. Without this
  // the source has no element behind it and the patch view draws every
  // follow(adc.amp, …) as a dangling cord — which is the whole reason the mic
  // object exists. A bare adc() still claims the effective uid, so a mic
  // placed with the default name is findable too.
  if (cueElement && !cueElement.dataset.oscillaUid) {
    cueElement.dataset.oscillaUid = uid || getAnalysisUid();
  }

  // off: rather than stop: — "stop" is a cue-name token in the grammar, so
  // adc(stop:1) will not parse. Sidestepped rather than fought.
  const wantsStop = p.off === 1 || p.off === "1" || p.off === true;
  if (wantsStop) {
    // Named cues stop only their own tap, so adc(uid:left, off:1) leaves the
    // right channel listening. A bare adc(off:1) releases the input entirely.
    stopAudioAnalysis(uid || undefined);
    releaseBinding(`adc-gain:${uid || getAnalysisUid()}`);
    if (uid) undrawInput(uid);
    else for (const name of [...drawn.keys()]) undrawInput(name);
    return true;
  }

  // its scope: a console line's in a box of its own, unless waveform:none;
  // a score's only when asked (waveform:self, into its element)
  const waveform = p.waveform != null ? String(p.waveform) : (cueElement ? "none" : "self");
  const draw = () => drawInput(uid || getAnalysisUid(), cueElement, waveform);

  // Keep the module default in step, so a later bare adc() reuses this name.
  if (uid) setAnalysisUid(uid);

  const ch = parseChannel(p.ch);
  if (isAudioAnalysisRunning(uid || undefined)) {
    // Re-firing an adc() should not tear down a working input mid-piece —
    // unless it is pointing the same uid at a different channel, which
    // startAudioAnalysis handles by replacing that one tap.
    if (uid === null || ch === "mix") { applyGain(uid || getAnalysisUid(), p.gain); applyFloor(uid || getAnalysisUid(), p.floor); draw(); return true; }
  }

  const name = uid || getAnalysisUid();
  markPending(name, true);
  try {
    const ok = await startAudioAnalysis({ uid, ch: p.ch });
    if (!ok) {
      // said in the live console too, however late: a prompt answered after
      // a while comes back long after the line ran
      const text = `[adc] ${inputTrouble() ?? "the input did not open"}`;
      console.warn(text);
      dispatchEvent(new CustomEvent("oscilla:livecode-say", { detail: { text, kind: "warn" } }));
    }
    else { applyGain(uid || getAnalysisUid(), p.gain); applyFloor(uid || getAnalysisUid(), p.floor); draw(); }
    return ok;
  } catch (err) {
    // Usually a refused or unavailable microphone. Say so plainly and let the
    // score carry on; anything bound to adc.* simply never moves.
    console.warn(`[cue:adc] could not open the audio input: ${err?.message ?? err}`);
    return false;
  } finally {
    markPending(name, false);
  }
}
